import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ShieldCheck } from "lucide-react";
import * as Icons from "lucide-react";
import { SPECIALTY_DETAILS, TRACK_BULLETS } from "../../data/content";

export const SpecialtyDrawer = ({ open, onClose, track, specialty, onBeginIntake }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const detail = track && specialty ? SPECIALTY_DETAILS[track.id]?.[specialty.name] : null;
  const bullets = track ? TRACK_BULLETS[track.id] || [] : [];
  const IconC = specialty ? Icons[specialty.icon] || Icons.Circle : Icons.Circle;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100] bg-[#0B1B2B]/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => e.target === e.currentTarget && onClose()}
          data-testid="specialty-drawer-overlay"
        >
          <motion.aside
            className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl border-l border-[#D4AF37]/30 overflow-y-auto"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            data-testid="specialty-drawer"
          >
            <div className="p-8 md:p-10">
              <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full text-[#64748B] hover:text-[#0B1B2B] hover:bg-[#F8FAFC] transition-colors" data-testid="specialty-drawer-close" aria-label="Close">
                <X size={18} />
              </button>
              <div className="w-12 h-12 rounded-xl bg-[#0B1B2B] flex items-center justify-center">
                <IconC size={22} className="text-[#D4AF37]" />
              </div>
              <p className="mt-6 text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-semibold">{track?.overline} · Specialty Track</p>
              <h3 className="mt-2 font-serif text-2xl md:text-3xl text-[#0B1B2B] tracking-tight" data-testid="specialty-drawer-title">{specialty?.name}</h3>
              <p className="mt-5 text-[#64748B] leading-relaxed">{detail || track?.description}</p>
              <div className="mt-8 space-y-4">
                {bullets.map((b) => (
                  <div key={b} className="flex gap-3 items-start">
                    <ShieldCheck size={17} className="text-[#D4AF37] mt-0.5 shrink-0" />
                    <span className="text-sm text-[#0B1B2B]">{b}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => { onBeginIntake(track.id, specialty.name); onClose(); }}
                className="mt-10 w-full bg-[#D4AF37] text-[#0B1B2B] font-semibold py-3.5 rounded-xl hover:bg-[#e8c968] hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 group"
                data-testid="specialty-drawer-intake-button"
              >
                Begin {specialty?.name} Intake
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="mt-4 text-xs text-[#64748B] text-center">Encrypted submission · Executive review within 1–2 business days</p>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
